import { createTheme } from '@mui/material';
import { KLYDO_BLACK, KLYDO_YELLOW } from '../consts/colors';
import {
  LARGE_SCREEN,
  MEDUIM_SCREEN,
  MOBILE,
  SMALL_SCREEN,
  SMALL_TABLET,
  TABLET,
} from '../consts/breakpoints';

declare module '@mui/material/styles' {
  interface Theme {
    dragAndDrop: {
      transition: string;
      draggedOpacity: number;
    };
  }

  interface ThemeOptions {
    dragAndDrop?: {
      transition?: string;
      draggedOpacity?: number;
    };
  }

  interface ZIndex {
    pendulumRod: number;
    pendulumBall: number;
    clockDials: number;
    handMinute: number;
    handMinuteS: number;
    handHour: number;
    handHourS: number;
  }

  interface BreakpointOverrides {
    xxs: true;
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
  }

  interface Palette {
    paper: {
      elevation2: string;
      elevation3: string;
      elevation4: string;
      elevation5: string;
      elevation6: string;
      elevation8: string;
      elevation10: string;
      elevation12: string;
      elevation24: string;
    };
    white: {
      5: string;
      8: string;
      9: string;
      10: string;
      12: string;
      16: string;
      20: string;
      24: string;
      56: string;
      70: string;
      90: string;
    };
    black: {
      19: string;
      50: string;
      80: string;
    };
    gray: {
      119: string;
    };
  }

  interface PaletteOptions {
    paper?: {
      elevation2: string;
      elevation3: string;
      elevation4: string;
      elevation5: string;
      elevation6: string;
      elevation8: string;
      elevation10: string;
      elevation12: string;
      elevation24: string;
    };
    white?: {
      5: string;
      8: string;
      9: string;
      10: string;
      12: string;
      16: string;
      20: string;
      24: string;
      56: string;
      70: string;
      90: string;
    };
    black?: {
      19: string;
      50: string;
      80: string;
    };
    gray?: {
      119: string;
    };
  }

  interface TypographyVariants {
    label: React.CSSProperties;
    value: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    label?: React.CSSProperties;
    value?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    label: true;
    value: true;
  }
}

const theme = createTheme({
  breakpoints: {
    values: {
      xxs: 0,
      xs: MOBILE,
      sm: SMALL_TABLET,
      md: TABLET,
      lg: SMALL_SCREEN,
      xl: MEDUIM_SCREEN,
      xxl: LARGE_SCREEN,
    },
  },
  palette: {
    mode: 'dark',
    action: {
      disabled: 'rgba(255, 255, 255, 0.12)',
      active: 'rgba(255, 255, 255, 0.56)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
    },
    primary: {
      main: KLYDO_YELLOW,
      contrastText: KLYDO_BLACK,
      light: '#FFDB57',
    },
    secondary: {
      main: '#2D2D2D',
      contrastText: 'rgba(255, 255, 255, 1)',
      light: '#494848',
      dark: '#1F1F1F',
    },
    background: {
      default: KLYDO_BLACK,
      paper: KLYDO_BLACK,
    },
    paper: {
      elevation2: '#232323',
      elevation3: '#252525',
      elevation4: '#272727',
      elevation5: '#2A2A2A',
      elevation6: '#2C2C2C',
      elevation8: '#2E2E2E',
      elevation10: '#313131',
      elevation12: '#333333',
      elevation24: '#383838',
    },
    white: {
      5: 'rgba(255, 255, 255, 0.05)',
      8: 'rgba(255, 255, 255, 0.08)',
      9: 'rgba(255, 255, 255, 0.09)',
      10: 'rgba(255, 255, 255, 0.1)',
      12: 'rgba(255, 255, 255, 0.12)',
      16: 'rgba(255, 255, 255, 0.16)',
      20: 'rgba(255, 255, 255, 0.20)',
      24: 'rgba(255, 255, 255, 0.24)',
      56: 'rgba(255, 255, 255, 0.56)',
      70: 'rgba(255, 255, 255, 0.70)',
      90: 'rgba(255, 255, 255, 0.90)',
    },
    black: {
      19: 'rgba(0, 0, 0, 0.19)',
      50: 'rgba(0, 0, 0, 0.5)',
      80: 'rgba(0, 0, 0, 0.8)',
    },
    gray: {
      119: 'rgba(119, 119, 119, 1)',
    },
  },
  typography: {
    fontFamily: [
      'Gilroy-bold, Gilroy-SemiBold, Gilroy-Medium, Gilroy-Regular, Gilroy-Light',
    ].join(','),
    button: {
      textTransform: 'capitalize',
      fontFamily: 'Gilroy-SemiBold',
      fontWeight: 500,
    },
    label: {
      fontFamily: 'Gilroy-Medium',
      fontSize: '14px',
      lineHeight: 'normal',
      fontWeight: 400,
      color: 'rgba(255, 255, 255, 0.65)',
    },
    body1: {
      fontFamily: 'Gilroy-Medium',
      fontSize: '16px',
      lineHeight: '150%',
      fontWeight: 400,
      color: 'rgba(255, 255, 255, 0.7)',
    },
    body2: {
      fontFamily: 'Gilroy-Medium',
      fontSize: '14px',
      lineHeight: '143%',
      fontWeight: 400,
      color: 'rgba(255, 255, 255, 0.7)',
    },
    h1: {
      fontFamily: 'Gilroy-Bold',
      lineHeight: '117%',
      fontSize: '96px',
      fontWeight: 300,
    },
    h2: {
      fontFamily: 'Gilroy-Bold',
      lineHeight: '120%',
      fontSize: '60px',
      fontWeight: 300,
    },
    h3: {
      fontFamily: 'Gilroy-Bold',
      lineHeight: '56px',
      fontSize: '48px',
      fontWeight: 400,
      color: 'rgba(255, 255, 255, 1)',
    },
    h4: {
      fontFamily: 'Gilroy-SemiBold',
      fontSize: '34px',
      lineHeight: '42px',
      fontWeight: 400,
    },
    h5: {
      fontFamily: 'Gilroy-SemiBold',
      fontSize: '24px',
      lineHeight: '32px',
      fontWeight: 400,
    },
    h6: {
      fontFamily: 'Gilroy-SemiBold',
      fontSize: '19px',
      lineHeight: '120%',
      fontWeight: 500,
    },
    subtitle1: {
      fontFamily: 'Gilroy-Bold',
      fontSize: '18px',
      lineHeight: '120%',
      fontWeight: 400,
    },
    subtitle2: {
      fontFamily: 'Gilroy-SemiBold',
      fontSize: '14px',
      lineHeight: '157%',
      fontWeight: 500,
    },
    value: {
      fontFamily: 'Gilroy-Bold',
      fontSize: '16px',
      lineHeight: '150%',
      fontWeight: 400,
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '1000px',
          boxShadow:
            '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow:
              '0px 2px 8px 0px rgba(0, 0, 0, 0.15), 0px 3px 4px 0px rgba(0, 0, 0, 0.18), 0px 4px 2px -2px rgba(0, 0, 0, 0.22)',
          },
          '&.Mui-disabled': {
            pointerEvents: 'auto',
            cursor: 'not-allowed',
          },
        },
        sizeLarge: {
          fontSize: '16px',
          lineHeight: '24px',
          padding: '16px 40px',
          minHeight: '56px',
        },
        sizeMedium: {
          fontSize: '14px',
          lineHeight: '20px',
          padding: '12px 32px',
          minHeight: '44px',
        },
        sizeSmall: {
          fontSize: '12px',
          lineHeight: '16px',
          padding: '8px 24px',
          minHeight: '32px',
        },
        textPrimary: {
          color: '#F7E35B',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          '&:focus': {
            backgroundColor: 'transparent',
          },
          '&:active': {
            backgroundColor: 'transparent',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
            backgroundColor: 'transparent',
          },
        },
        textSecondary: {
          color: '#FFFFFF',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          '&:focus': {
            backgroundColor: 'transparent',
          },
          '&:active': {
            backgroundColor: 'transparent',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },
  zIndex: {
    pendulumRod: 1,
    pendulumBall: 2,
    clockDials: 0,
    handMinute: 3,
    handMinuteS: 2,
    handHour: 3,
    handHourS: 2,
  },
  dragAndDrop: {
    transition:
      'transform 0.5s cubic-bezier(1, 0, 0, 1), opacity 0.5s ease, border 0.2s ease',
    draggedOpacity: 0.4,
  },
});

export default theme;
